import type {
  TrackingTextField,
  TrackingTextRule,
} from "@redotech/redo-order-tracking-settings-common/tracking-text-rule";
import { escapeRegExp } from "@redotech/util/string";
import type { TrackingLocation } from "./order-tracking-tracking-location-definition";

export interface TrackingTextResult {
  text: string;
  matched: boolean;
}

export function applyTrackingTextRules(
  value: string | null | undefined,
  target: TrackingTextField,
  rules: readonly TrackingTextRule[] = [],
): TrackingTextResult {
  if (!value) {
    return { text: "", matched: false };
  }

  const replacements: { start: number; end: number; text: string }[] = [];
  for (const rule of rules) {
    if (rule.target !== target && rule.target !== "all") {
      continue;
    }
    const phrases = rule.phrases
      .map((phrase) => phrase.trim())
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)
      .map((phrase) => phrase.split(/\s+/).map(escapeRegExp).join("\\s+"));
    if (phrases.length === 0) {
      continue;
    }
    const pattern = new RegExp(
      `(?<![\\p{L}\\p{M}\\p{N}_])(?:${phrases.join("|")})(?![\\p{L}\\p{M}\\p{N}_])`,
      "giu",
    );
    for (const match of value.matchAll(pattern)) {
      if (rule.replacement === "") {
        return { text: "", matched: true };
      }
      const start = match.index;
      const end = start + match[0].length;
      // Match the original input once; earlier rules own overlapping spans.
      if (!replacements.some((span) => start < span.end && end > span.start)) {
        replacements.push({ start, end, text: rule.replacement });
      }
    }
  }

  let text = "";
  let cursor = 0;
  for (const replacement of replacements.sort((a, b) => a.start - b.start)) {
    text += value.slice(cursor, replacement.start) + replacement.text;
    cursor = replacement.end;
  }
  return { text: text + value.slice(cursor), matched: replacements.length > 0 };
}

export function applyTrackingLocationRules(
  location: TrackingLocation | null | undefined,
  rules: readonly TrackingTextRule[] = [],
): TrackingTextResult {
  if (typeof location === "string" || location == null) {
    return applyTrackingTextRules(location, "location", rules);
  }

  const parts = [location.city, location.state, location.country].map((part) =>
    applyTrackingTextRules(part, "location", rules),
  );
  const postalCode = applyTrackingTextRules(
    location.postalCode,
    "location",
    rules,
  );
  return {
    text: parts
      .map((part) => part.text)
      .filter((text) => text.trim())
      .join(", "),
    matched: postalCode.matched || parts.some((part) => part.matched),
  };
}
